/* eslint-disable @typescript-eslint/no-explicit-any */
import { ApiSchema } from "@api-types";
import { env } from "env";
import { toast } from "components/ToastProvider/ToastProvider";
import { from, Observable, of, Subject, switchMap, takeUntil } from "rxjs";
import { fromFetch } from "rxjs/fetch";
import { ValuesType } from "utility-types";

type AuthReqOptions = {
  includeCredentials?: boolean;
  cors?: RequestMode;
  raw?: boolean;
  res?: "json" | "text" | "null";
  type?: "application/json" | "application/binary" | "application/text";
  errorHandlers?: {
    [key in string]: (err: object) => void;
  };
  errorHandler?: (res: Response, err: AuthError) => void | Observable<any>;
};

type PathTypes = ApiSchema.AuthGetPathTypes &
  ApiSchema.AuthPostPathTypes &
  ApiSchema.AuthPatchPathTypes &
  ApiSchema.AuthDeletePathTypes &
  ApiSchema.AuthGetCountPathTypes &
  ApiSchema.AuthGetOnePathTypes &
  ApiSchema.AuthPostOnePathTypes &
  ApiSchema.AuthPatchOnePathTypes &
  ApiSchema.AuthDeleteOnePathTypes &
  ApiSchema.AuthFuncPathTypes &
  ApiSchema.AuthActPathTypes;
type ErrorPathTypes = ApiSchema.AuthGetErrorTypes &
  ApiSchema.AuthPostErrorTypes &
  ApiSchema.AuthPatchErrorTypes &
  ApiSchema.AuthDeleteErrorTypes &
  ApiSchema.AuthGetCountErrorTypes &
  ApiSchema.AuthGetOneErrorTypes &
  ApiSchema.AuthPostOneErrorTypes &
  ApiSchema.AuthPatchOneErrorTypes &
  ApiSchema.AuthDeleteOneErrorTypes &
  ApiSchema.AuthFuncErrorTypes &
  ApiSchema.AuthActErrorTypes;

type RequestBodyTypes = ApiSchema.AuthPostRequestBody &
  ApiSchema.AuthPatchRequestBody &
  ApiSchema.AuthDeleteRequestBody;

type ParamValueType = string | number | number[] | string[] | boolean | Date | null | undefined;

class AuthBuilderLevel0<T, R = T, TReqKey extends keyof ErrorPathTypes = keyof ErrorPathTypes> {
  private errorHandlers: { [key in string]: Function } = {};

  public catchClientError = <TTypeName extends ErrorPathTypes[TReqKey]["typename"]>(
    key: TTypeName,
    handler: (error: Extract<ErrorPathTypes[TReqKey], { typename: TTypeName }>) => void,
  ) => {
    this.errorHandlers[key] = handler;
    return this;
  };

  public catchErrorByStatus = (statusCode: number, errorHandler: Function) => {
    this.errorHandlers[`${statusCode}`] = errorHandler;
    return this;
  };

  private readonly authCall$ = <Res>(
    url: string,
    method: "GET" | "POST" | "DELETE" | "PATCH" | "PUT",
    params?: { [key in string]: string | number | object | boolean } | null,
    body?: object | null,
    options?: AuthReqOptions,
  ): Observable<Res> => {
    const reqUrl = new URL(url, env.VITE_API_BASEURL ?? window.location.origin);
    if (params) {
      Object.keys(params).forEach((key) => {
        reqUrl.searchParams.append(key, `${params[key]}`);
      });
    }
    const cancelToken$ = new Subject<boolean>();

    const req = new Request(reqUrl, {
      method,
      mode: options?.cors,
      credentials: options?.includeCredentials ? "include" : undefined,
      body: body ? (body instanceof FormData || options?.raw ? (body as never) : JSON.stringify(body)) : null,
    });
    req.headers.set("Content-Type", options?.type || "application/json");

    return fromFetch(req).pipe(
      switchMap((res) => {
        if (!res) return of(null) as Observable<Res>;

        if (res.status === 204) {
          return of(null) as Observable<Res>;
        } else if (res.ok) {
          if (!options?.res || options?.res === "json") {
            return from(res.json()) as Observable<Res>;
          } else if (options?.res === "text") {
            return from(res.text()) as Observable<Res>;
          } else {
            return of(null) as Observable<Res>;
          }
        } else {
          return from(res.text()).pipe(
            switchMap((text) => {
              const err = this.getAuthError(text, res);
              let res$: Observable<Res | null> | null = null;
              if (options?.errorHandlers && err.typename && !!options?.errorHandlers[err.typename]) {
                options.errorHandlers[err.typename]!(err.detail);
              } else if (options?.errorHandlers && !!options?.errorHandlers[`${res.status}`]) {
                options.errorHandlers[`${res.status}`]!(err);
              } else if (options?.errorHandlers && options?.errorHandlers["*"]) {
                options.errorHandlers["*"](err);
              } else if (options?.errorHandler) {
                res$ = options.errorHandler(res, err) ?? null;
              } else {
                //TODO make Detail component with traceId and message
                toast({
                  severity: "error",
                  summary: "An error has occured",
                  detail: err.message || err.statusText || "",
                  sticky: true,
                });
              }
              cancelToken$.next(true);
              return (res$ as never) || (of(null) as never);
            }),
          );
        }
      }),
      takeUntil(cancelToken$),
    );
  };
  public getAuthError = (text: string, res: Response) => {
    const traceId = res.headers.get("x-error-trace-id") ?? undefined;
    try {
      const detail = JSON.parse(text);
      return new AuthError(res.statusText, res.status, detail, traceId);
    } catch (_e) {
      return new AuthError(
        res.statusText,
        res.status,
        {
          message: text,
        },
        traceId,
      );
    }
  };

  public $ = <AdditonalProps extends {}>() => {
    const url = this.embededParams
      ? this.setParams(this.path[this.op] || "", this.embededParams)
      : this.path[this.op];
    return this.authCall$<object>(
      `${url}`,
      this.method || "GET",
      null,
      this.body,
      // by default auth query have no auth and no client id
      {
        ...Object.assign(this.options, { errorHandlers: this.errorHandlers }),
      },
    ) as Observable<R extends Array<unknown> ? (ValuesType<R> & AdditonalProps)[] : R & AdditonalProps>;
  };

  constructor(
    private readonly path: { [key in string]: string },
    private readonly op: keyof PathTypes,
    private readonly body?: object | null,
    private readonly method?: "GET" | "POST" | "PATCH" | "DELETE",
    private options: AuthReqOptions = {},
    private readonly embededParams?: { [key in string]: ParamValueType },
  ) {}

  protected setParams = (url: string, params: { [key in string]: ParamValueType }) => {
    Object.keys(params).forEach((p) => {
      url = url.replace("${" + p + "}", String(this.getParamValue(params[p])));
    });
    url = url.replace(/\$\{(.*?)\}/g, "null");
    return url;
  };

  protected getParamValue = (param?: ParamValueType) => {
    if (param === null || param === undefined) {
      return null;
    } else if (param instanceof Date) {
      return `${encodeURIComponent(param?.toISOString() || "")}`;
    } else if (typeof param === "string" || param instanceof String) {
      return `${encodeURIComponent(param?.toString() || "")}`;
    } else if (Array.isArray(param)) {
      return JSON.stringify(param);
    } else if (param === true || param === false) {
      return param ? "true" : "false";
    }
    return param;
  };

  public debug = () => {
    return JSON.stringify(this);
  };
}

export const authAct = <
  K extends keyof ApiSchema.AuthActPathTypes = keyof ApiSchema.AuthActPathTypes,
  V = ApiSchema.AuthActPathTypes[K],
>(
  op: K,
  body: K extends keyof RequestBodyTypes ? RequestBodyTypes[K] : object,
  params?: ApiSchema.AuthActParams[K],
  options: AuthReqOptions = {},
) => {
  options.includeCredentials = true;
  type T = V;
  const qb = new AuthBuilderLevel0<T, T, K>(
    ApiSchema.authActPaths,
    op,
    body,
    "POST",
    options as never,
    params,
  );
  return qb;
};

export const authFuncList = <
  K extends keyof ApiSchema.AuthFuncPathTypes = keyof ApiSchema.AuthFuncPathTypes,
  V = ApiSchema.AuthFuncPathTypes[K],
>(
  op: K,
  params?: ApiSchema.AuthFuncParams[K],
  options: AuthReqOptions = {},
) => {
  type T = V extends Array<unknown> ? ValuesType<V> : V;
  const qb = new AuthBuilderLevel0<T, T[], K>(ApiSchema.authFuncPaths, op, null, "GET", options, params);
  return qb;
};

export const authFuncOne = <
  K extends keyof ApiSchema.AuthFuncPathTypes = keyof ApiSchema.AuthFuncPathTypes,
  V = ApiSchema.AuthFuncPathTypes[K],
>(
  op: K,
  params?: ApiSchema.AuthFuncParams[K],
  options: AuthReqOptions = {},
) => {
  type T = V;
  const qb = new AuthBuilderLevel0<T, T, K>(ApiSchema.authFuncPaths, op, null, "GET", options, params);
  return qb;
};

export class AuthError {
  public readonly typename: string | null;
  public readonly detail: any;
  public readonly message?: string | null;

  constructor(
    public readonly statusText: string,
    public readonly status: number,

    detail: any,
    public readonly traceId?: string,
  ) {
    this.status = status;
    this.detail = detail;
    this.typename = detail.typename || null;

    this.message = detail?.message || detail?.error?.message || detail?.statusText;
  }
}
